import React, { useState, useEffect } from 'react'
import { AddressManualEntryFieldsetStyled } from './AddressManualEntry.styled'
import { ValidationResult } from '../../../../component-library/src/types'
import {
  AutocompleteInputField,
  AutocompleteInputProps,
} from '@mobi/component-library/Common/Input/AutocompleteInputField/AutocompleteInputField'
import { Input } from '@mobi/component-library/Common/V2'
import { searchStreetTypes, searchSuburbStatePostcodes } from '@mobi/account/Common'
import { getRequiredValidationMessage } from '@mobi/account/Areas/SignUp/Utils'
import type { AccountHolderAddress } from '@mobi/api-types'
import { extractSuburbStatePostcode } from '@mobi/utils'
import { Nationality } from './utils'

type AddressManualEntryProps = {
  inputNamePrefix?: string
  onValidationChange?: (result: ValidationResult<AccountHolderAddress>) => void
  isDisabled?: boolean
}

export const AddressManualEntry = ({
  inputNamePrefix = '',
  onValidationChange,
  isDisabled = false,
}: AddressManualEntryProps) => {
  const [unitNumber, setUnitNumber] = useState('')
  const [streetNumber, setStreetNumber] = useState('')
  const [streetName, setStreetName] = useState('')
  const [streetType, setStreetType] = useState('')
  const [suburbStateAndPostcode, setSuburbStateAndPostcode] = useState('')
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})

  const checkForNonNegativeUnitStreetNumber = (val: string) => {
    return val === '' || /^[1-9][a-zA-Z0-9]*$/.test(val)
  }

  useEffect(() => {
    if (streetNumber && streetName && streetType && suburbStateAndPostcode) {
      const [suburb, state, postcode] = extractSuburbStatePostcode(suburbStateAndPostcode)
      onValidationChange?.({
        isValid: true,
        result: {
          unitNumber,
          streetNumber,
          streetName,
          streetType,
          suburb,
          postcode,
          state,
          country: Nationality.Australia,
        },
      })
    } else {
      onValidationChange?.({
        isValid: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitNumber, streetNumber, streetName, streetType, suburbStateAndPostcode])

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  return (
    <AddressManualEntryFieldsetStyled>
      <Input
        label='Unit Number'
        name={`${inputNamePrefix}unit-number`}
        placeholder='Enter your unit number'
        type='text'
        inputMode='text'
        value={unitNumber}
        onChange={e => {
          const val = e.currentTarget.value
          if (checkForNonNegativeUnitStreetNumber(val)) {
            setUnitNumber(val)
          }
        }}
        isDisabled={isDisabled}
      />
      <Input
        label='Street Number'
        name={`${inputNamePrefix}street-number`}
        placeholder='Enter your street number'
        type='text'
        inputMode='text'
        value={streetNumber}
        onChange={e => {
          const val = e.currentTarget.value
          if (checkForNonNegativeUnitStreetNumber(val)) {
            setStreetNumber(val)
          }
        }}
        required
        onFocus={() => resetFieldValidation('streetNumber')}
        onBlur={() => {
          setValidationMessages(prev => ({
            ...prev,
            streetNumber: getRequiredValidationMessage(streetNumber),
          }))
        }}
        isInvalid={!!validationMessages.streetNumber}
        helpText={validationMessages.streetNumber}
        isDisabled={isDisabled}
      />
      <Input
        label='Street Name'
        name={`${inputNamePrefix}street-name`}
        placeholder='Enter your street name'
        value={streetName}
        onChange={e => setStreetName(e.currentTarget.value)}
        required
        onFocus={() => resetFieldValidation('streetName')}
        onBlur={() => {
          setValidationMessages(prev => ({
            ...prev,
            streetName: getRequiredValidationMessage(streetName),
          }))
        }}
        isInvalid={!!validationMessages.streetName}
        helpText={validationMessages.streetName}
        isDisabled={isDisabled}
      />
      <AutocompleteInputField
        inputName={`${inputNamePrefix}street-type`}
        name='Street Type'
        placeholder='Enter your street type'
        required
        minChars={2}
        fetchSuggestionsCallback={withValueMapping(searchStreetTypes)}
        defaultValue={streetType}
        onSelect={setStreetType}
        onUnselect={() => setStreetType('')}
        renderInput={({
          inputName,
          name,
          ...props
        }: Omit<AutocompleteInputProps, 'defaultValue' | 'type'>) => (
          <Input
            {...props}
            name={inputName ?? 'autocomplete'}
            label={name}
            onFocus={() => resetFieldValidation('streetType')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                streetType: getRequiredValidationMessage(streetType),
              }))
            }}
            isInvalid={!!validationMessages.streetType}
            helpText={validationMessages.streetType}
            isDisabled={isDisabled}
          />
        )}
      />
      <AutocompleteInputField
        inputName={`${inputNamePrefix}suburb`}
        name='Suburb'
        placeholder='Enter your suburb'
        required
        fetchSuggestionsCallback={withValueMapping(searchSuburbStatePostcodes)}
        defaultValue={suburbStateAndPostcode}
        onSelect={setSuburbStateAndPostcode}
        onUnselect={() => setSuburbStateAndPostcode('')}
        renderInput={({
          inputName,
          name,
          ...props
        }: Omit<AutocompleteInputProps, 'defaultValue' | 'type'>) => (
          <Input
            {...props}
            name={inputName ?? 'autocomplete'}
            label={name}
            onFocus={() => resetFieldValidation('suburbStateAndPostcode')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                suburbStateAndPostcode: getRequiredValidationMessage(suburbStateAndPostcode),
              }))
            }}
            isInvalid={!!validationMessages.suburbStateAndPostcode}
            helpText={validationMessages.suburbStateAndPostcode}
            isDisabled={isDisabled}
          />
        )}
      />
    </AddressManualEntryFieldsetStyled>
  )
}

// =============
// Local Helpers
// =============

function withValueMapping(searchFn: (search: string) => Promise<{ value: string }[]>) {
  return (search: string) =>
    searchFn(search).then(results =>
      results.map(({ value }) => {
        return {
          id: value,
          value,
        }
      })
    )
}
